import Utils from "Utils/utils";
import { makeApi, URLS } from "Utils/apiURL";

/**
 * Data layer for the command palette's universal search. We deliberately reuse
 * the SAME endpoints the rest of the app already uses — no new APIs:
 *
 *   Contacts → GET  /cm/contacts/autocomplete?search=  (ContactAutoCompleteDropdown)
 *   Accounts → POST /cm/accounts/get                    (Accounts list page)
 *
 * Each fetch registers a cancel callback so that a stale in-flight request is
 * aborted the moment the user types again — older responses can never land
 * after a newer one (no flicker / out-of-order results).
 */

// Max rows surfaced per entity group in the palette (Contacts, Accounts).
export const ENTITY_RESULT_LIMIT = 3;

// One cancel slot per stream. Re-firing a stream cancels its previous request.
const cancelSlots = { contacts: null, accounts: null };

const cancelInFlight = key => {
  if (cancelSlots[key]) {
    cancelSlots[key]("cancel");
    cancelSlots[key] = null;
  }
};

const isCancelled = res =>
  res?.message?.message?.toLowerCase?.() === "cancel" ||
  res?.message?.toLowerCase?.() === "cancel";

const fullName = (firstName = "", lastName = "") =>
  Utils.getFullNameFromUserInfo({ firstName, LastName: lastName })?.trim() || "";

/**
 * Search contacts by keyword. Returns a normalized, capped list of palette
 * items: { id, type:"contact", name, email, company }.
 */
export const searchContacts = async query => {
  const term = (query || "").trim();
  if (!term) return [];

  cancelInFlight("contacts");
  const config = {
    url: `${URLS.getContactsBySearchKeywords}${encodeURIComponent(term)}`,
    method: "GET"
  };
  const cancel = value => {
    cancelSlots.contacts = value;
  };

  try {
    const res = await makeApi(config, cancel);
    if (isCancelled(res) || +res?.status !== 200) return [];
    const list = res?.data || [];
    return list
      .map(c => {
        const { id = "", email = "", firstName = "", LastName = "" } = c || {};
        const name = fullName(firstName, LastName) || email || "Unnamed contact";
        // BE may send any of these for the contact's company; all optional.
        const { companyName = "", accountName = "", company = "" } = c || {};
        return {
          id,
          type: "contact",
          name,
          email,
          company: companyName || accountName || company || ""
        };
      })
      .filter(c => c.id)
      .slice(0, ENTITY_RESULT_LIMIT);
  } catch (e) {
    return [];
  }
};

/**
 * Search accounts by keyword via the Accounts list endpoint. Returns a
 * normalized, capped list: { id, type:"account", name, domain }.
 */
export const searchAccounts = async query => {
  const term = (query || "").trim();
  if (!term) return [];

  cancelInFlight("accounts");
  const config = {
    url: URLS.getAccountList,
    method: "POST",
    data: {
      pageNo: 1,
      limit: ENTITY_RESULT_LIMIT,
      searchTerm: term,
      filterPayload: { stageId: "", memberIds: [], fields: [] }
    }
  };
  const cancel = value => {
    cancelSlots.accounts = value;
  };

  try {
    const res = await makeApi(config, cancel);
    if (isCancelled(res) || +res?.status !== 200) return [];
    const accounts = res?.data?.accounts || [];
    return accounts
      .map(a => {
        const { id = "", name = "", domain = "" } = a || {};
        const label = name || (domain ? domain.split(".")[0] : "") || "Unnamed account";
        return { id, type: "account", name: label, domain };
      })
      .filter(a => a.id)
      .slice(0, ENTITY_RESULT_LIMIT);
  } catch (e) {
    return [];
  }
};
